import React, {useMemo} from 'react';
import {type FieldPath, type FieldValues, useController} from 'react-hook-form';
import {
	ReactHookFormControl,
	type WithReactHookFormControlFieldProps,
} from '@netx/core-components/Form';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {splitEventHandler} from '@netx/core-next/utils';
import {combineRefs} from '@netx/core-next/utils/react';
import {type IPerson} from '../../schema';
import {PersonAutoList, type PersonAutoListProps} from './PersonAutoList';

export interface ReactHookFormPersonAutoListProps<
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends WithReactHookFormControlFieldProps<
	PersonAutoListProps<Multiple, DisableClearable>,
	TFieldValues,
	TName
> {
	valueLabelName?: string;
}

/**
 * `react-hook-form` wrapper for `PersonAutoList`
 */
export const ReactHookFormPersonAutoListPrimitive = <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	{
		control,
		valueLabelName,
		ControllerProps,
		...props
	}: ReactHookFormPersonAutoListProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
	ref: React.ForwardedRef<HTMLDivElement>,
) => {
	const fieldName = props.name || 'person-auto-list';

	return (
		<ReactHookFormControl<TFieldValues, TName>
			{...ControllerProps}
			control={control}
			name={fieldName as TName}
			render={({field, formState}) => {
				const handleChange = useConstCallback(
					splitEventHandler(props.onChange, (event, value) => {
						field.onChange(value);
					}),
				);

				return (
					<PersonAutoList<Multiple, DisableClearable>
						error={formState.errors[fieldName]?.message?.toString()}
						{...props}
						{...field}
						disabled={props.disabled || field.disabled}
						ref={combineRefs(ref, field.ref)}
						onChange={handleChange}
					/>
				);
			}}
			rules={{
				required: props.required,
				...ControllerProps?.rules,
			}}
		/>
	);
};

/**
 * `react-hook-form` wrapper for `PersonAutoList`
 */
export const ReactHookFormPersonAutoList = React.forwardRef(
	ReactHookFormPersonAutoListPrimitive,
) as <
	Multiple extends boolean = false,
	DisableClearable extends boolean = false,
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
	props: ReactHookFormPersonAutoListProps<
		Multiple,
		DisableClearable,
		TFieldValues,
		TName
	>,
) => JSX.Element;

export default ReactHookFormPersonAutoList;
